import * as React from "react"

import { accessibility as labels, disclosure } from "../copy"
import Disclosure from "../Disclosure"
import type { AccessibilityBucket } from "../types"

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-0.5 tw:px-3 tw:py-2 tw:md:flex-row tw:md:items-baseline tw:md:gap-4">
      <dt className="tw:w-40 tw:shrink-0 tw:text-sm tw:leading-5 tw:text-muted-foreground">{label}</dt>
      <dd className="tw:min-w-0 tw:text-sm tw:leading-5 tw:font-medium tw:break-words">{children}</dd>
    </div>
  )
}

const Mono = ({ children }: { children: React.ReactNode }) => (
  <code className="tw:font-mono tw:text-xs tw:break-all">{children}</code>
)

export default function AccessibilityDetails({ bucket }: { bucket: AccessibilityBucket }) {
  const fetched = bucket.ourFetch ?? {}
  const caching = bucket.caching ?? {}
  const headers = fetched.headers ?? {}
  const headerNames = Object.keys(headers)

  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      {/* A definition list, not a Table: these are term/value pairs, and the
          house table pattern would only add a border at the cost of correct
          semantics. The framing matches it instead. */}
      <dl className="tw:divide-y tw:divide-border tw:overflow-hidden tw:rounded-lg tw:border tw:border-border tw:bg-card">
        {fetched.httpCode !== undefined && (
          <Row label={labels.httpStatus}>{fetched.httpCode}</Row>
        )}
        {bucket.finalUrl && (
          <Row label={labels.finalUrl}>
            <Mono>{bucket.finalUrl}</Mono>
          </Row>
        )}
        {bucket.httpUpgraded && <Row label={labels.upgraded}>{labels.upgradedTo}</Row>}
        {fetched.server && <Row label={labels.server}>{fetched.server}</Row>}
        {fetched.contentType && <Row label={labels.contentType}>{fetched.contentType}</Row>}
        {/* A failed fetch stores 0, and "Size 0 KB" reads as a measurement
            rather than an absence. */}
        {fetched.feedSize !== undefined && fetched.feedSize > 0 && (
          <Row label={labels.size}>{Math.round(fetched.feedSize / 1024)} KB</Row>
        )}
        {caching.cacheControl && <Row label={labels.cacheControl}>{caching.cacheControl}</Row>}
        {caching.age !== null && caching.age !== undefined && (
          <Row label={labels.age}>{caching.age}s</Row>
        )}
        {caching.xCache && <Row label={labels.xCache}>{caching.xCache}</Row>}
        {caching.etag && (
          <Row label={labels.etag}>
            <Mono>{caching.etag}</Mono>
          </Row>
        )}
        {caching.expires && <Row label={labels.expires}>{caching.expires}</Row>}
      </dl>

      {headerNames.length > 0 && (
        <Disclosure label={disclosure.headers}>
          <dl className="tw:divide-y tw:divide-border tw:overflow-hidden tw:rounded-lg tw:border tw:border-border tw:bg-card">
            {headerNames.map((name) => (
              <div
                key={name}
                className="tw:flex tw:flex-col tw:gap-0.5 tw:px-3 tw:py-2 tw:md:flex-row tw:md:gap-4"
              >
                <dt className="tw:w-56 tw:shrink-0 tw:font-mono tw:text-xs tw:leading-5 tw:text-muted-foreground">
                  {name}
                </dt>
                <dd className="tw:min-w-0 tw:font-mono tw:text-xs tw:leading-5 tw:break-all">
                  {String(headers[name])}
                </dd>
              </div>
            ))}
          </dl>
        </Disclosure>
      )}
    </div>
  )
}
